import { Fragment } from 'react';
import { Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';

import { BotaoAcaoLinha } from '../../components/BotaoAcaoLinha';
import { BotaoAdicionar } from '../../components/RelatorioCivelComponents';
import type { RelatorioCivelStepProps } from './relatorio-civel-step-props';
import type { AtendimentoPresencialItem, AtendimentoRemotoItem } from '../relatorio-civel-page.models';

function parseTotalNaoNegativo(value: string): number {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
}

export default function Step10Atendimentos(props: RelatorioCivelStepProps) {
  const {
    cardStyle,
    headerStyle,
    RELATORIO_CORES,
    isViewMode,
    formData,
    setFormData,

    // Presenciais
    arquivoAtendimentoPresencialInputRef,
    handleFileChangeAtendimentoPresencial,
    novoArquivoAtendimentoPresencial,
    novaObservacaoAtendimentoPresencial,
    setNovaObservacaoAtendimentoPresencial,
    adicionarAtendimentoPresencial,
    editandoAtendimentoPresencial,
    arquivoAtendimentoPresencialEdicaoInputRef,
    handleFileChangeAtendimentoPresencialEdicao,
    valorEdicaoArquivoAtendimentoPresencial,
    valorEdicaoObservacaoAtendimentoPresencial,
    setValorEdicaoObservacaoAtendimentoPresencial,
    salvarEdicaoAtendimentoPresencial,
    cancelarEdicaoAtendimentoPresencial,
    iniciarEdicaoAtendimentoPresencial,
    removerAtendimentoPresencial,

    // Remotos
    arquivoAtendimentoRemotoInputRef,
    handleFileChangeAtendimentoRemoto,
    novoArquivoAtendimentoRemoto,
    novaObservacaoAtendimentoRemoto,
    setNovaObservacaoAtendimentoRemoto,
    adicionarAtendimentoRemoto,
    editandoAtendimentoRemoto,
    arquivoAtendimentoRemotoEdicaoInputRef,
    handleFileChangeAtendimentoRemotoEdicao,
    valorEdicaoArquivoAtendimentoRemoto,
    valorEdicaoObservacaoAtendimentoRemoto,
    setValorEdicaoObservacaoAtendimentoRemoto,
    salvarEdicaoAtendimentoRemoto,
    cancelarEdicaoAtendimentoRemoto,
    iniciarEdicaoAtendimentoRemoto,
    removerAtendimentoRemoto,
  } = props;

  const totalPresenciais = formData.atendimento.totalPresenciais ?? 0;
  const totalRemotas = formData.atendimento.totalRemotas ?? 0;

  const atualizarTotalPresenciais = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      atendimento: {
        ...prev.atendimento,
        totalPresenciais: parseTotalNaoNegativo(value),
      },
    }));
  };

  const atualizarTotalRemotas = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      atendimento: {
        ...prev.atendimento,
        totalRemotas: parseTotalNaoNegativo(value),
      },
    }));
  };

  return (
    <Fragment>
      {/* Sub-etapa 1: Atendimentos Presenciais */}
      <Card className="mb-4" style={cardStyle}>
        <Card.Header style={headerStyle}>
          <h6 className="mb-0">
            <i className="bi bi-person-check me-2"></i>
            Atendimentos Presenciais
          </h6>
        </Card.Header>
        <Card.Body>
          {!isViewMode ? (
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">Arquivo</Form.Label>
                  <Form.Control
                    type="file"
                    ref={arquivoAtendimentoPresencialInputRef}
                    onChange={handleFileChangeAtendimentoPresencial}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  <Form.Text className="text-muted">Arquivo de até 10MB</Form.Text>
                  {novoArquivoAtendimentoPresencial && (
                    <div className="mt-2">
                      <Badge style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                        {novoArquivoAtendimentoPresencial.name} (
                        {(novoArquivoAtendimentoPresencial.size / 1024 / 1024).toFixed(2)} MB)
                      </Badge>
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="fw-bold">Total</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step={1}
                    value={totalPresenciais}
                    onChange={(e) => atualizarTotalPresenciais(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">Observação</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Observações (opcional)"
                    value={novaObservacaoAtendimentoPresencial}
                    onChange={(e) => setNovaObservacaoAtendimentoPresencial(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <BotaoAdicionar className="w-100" onClick={adicionarAtendimentoPresencial} />
              </Col>
            </Row>
          ) : (
            <Row className="mb-3">
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="fw-bold">Total</Form.Label>
                  <Form.Control type="number" min={0} value={totalPresenciais} disabled />
                </Form.Group>
              </Col>
            </Row>
          )}

          <Table bordered hover>
            <thead className="table-light">
              <tr>
                <th style={{ width: '5%' }}>#</th>
                <th style={{ width: '35%' }}>Arquivo</th>
                <th style={{ width: '10%' }}>Total</th>
                <th style={{ width: '35%' }}>Observação</th>
                {!isViewMode && <th style={{ width: '15%' }}>Ações</th>}
              </tr>
            </thead>
            <tbody>
              {formData.atendimento.presenciais.length === 0 ? (
                <tr>
                  <td colSpan={isViewMode ? 4 : 5} className="text-center text-muted">
                    Nenhum atendimento presencial adicionado
                  </td>
                </tr>
              ) : (
                formData.atendimento.presenciais.map((item: AtendimentoPresencialItem, index: number) => {
                  const estaEditando = editandoAtendimentoPresencial === item.id;

                  return (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>
                        {estaEditando ? (
                          <div>
                            <Form.Control
                              type="file"
                              size="sm"
                              ref={arquivoAtendimentoPresencialEdicaoInputRef}
                              onChange={handleFileChangeAtendimentoPresencialEdicao}
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              style={{ borderRadius: '8px' }}
                            />
                            {valorEdicaoArquivoAtendimentoPresencial && (
                              <small className="text-muted d-block mt-1">
                                {valorEdicaoArquivoAtendimentoPresencial.name} (
                                {(valorEdicaoArquivoAtendimentoPresencial.size / 1024 / 1024).toFixed(2)} MB)
                              </small>
                            )}
                            {!valorEdicaoArquivoAtendimentoPresencial && item.arquivoNome && (
                              <small className="text-muted d-block mt-1">Arquivo atual: {item.arquivoNome}</small>
                            )}
                          </div>
                        ) : item.arquivoNome ? (
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-file-earmark"></i>
                            <span>{item.arquivoNome}</span>
                            {item.arquivo && (
                              <Badge style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                                {(item.arquivo.size / 1024 / 1024).toFixed(2)} MB
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">Sem arquivo</span>
                        )}
                      </td>
                      {index === 0 && (
                        <td
                          className="text-center align-middle"
                          rowSpan={formData.atendimento.presenciais.length}
                        >
                          {totalPresenciais}
                        </td>
                      )}
                      <td>
                        {estaEditando ? (
                          <Form.Control
                            type="text"
                            size="sm"
                            placeholder="Observações (opcional)"
                            value={valorEdicaoObservacaoAtendimentoPresencial}
                            onChange={(e) => setValorEdicaoObservacaoAtendimentoPresencial(e.target.value)}
                            style={{ borderRadius: '8px' }}
                          />
                        ) : (
                          item.observacao || '-'
                        )}
                      </td>
                      {!isViewMode && (
                        <td>
                          {estaEditando ? (
                            <div className="d-flex align-items-center justify-content-center flex-nowrap gap-1">
                              <BotaoAcaoLinha tipo="salvar" onClick={() => salvarEdicaoAtendimentoPresencial(item.id)} />
                              <BotaoAcaoLinha tipo="cancelar" onClick={cancelarEdicaoAtendimentoPresencial} />
                            </div>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center flex-nowrap gap-1">
                              <BotaoAcaoLinha tipo="editar" onClick={() => iniciarEdicaoAtendimentoPresencial(item)} />
                              <BotaoAcaoLinha tipo="excluir" onClick={() => removerAtendimentoPresencial(item.id)} />
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
          <div className="mt-2 text-end">
            <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
              <small className="text-muted">Total:</small> {totalPresenciais}
            </h5>
          </div>
        </Card.Body>
      </Card>

      {/* Sub-etapa 2: Atendimentos Remotos */}
      <Card className="mb-4" style={cardStyle}>
        <Card.Header style={headerStyle}>
          <h6 className="mb-0">
            <i className="bi bi-telephone me-2"></i>
            Atendimentos Remotos
          </h6>
        </Card.Header>
        <Card.Body>
          {!isViewMode ? (
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">Arquivo</Form.Label>
                  <Form.Control
                    type="file"
                    ref={arquivoAtendimentoRemotoInputRef}
                    onChange={handleFileChangeAtendimentoRemoto}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  <Form.Text className="text-muted">Arquivo de até 10MB</Form.Text>
                  {novoArquivoAtendimentoRemoto && (
                    <div className="mt-2">
                      <Badge style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                        {novoArquivoAtendimentoRemoto.name} ({(novoArquivoAtendimentoRemoto.size / 1024 / 1024).toFixed(2)} MB)
                      </Badge>
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="fw-bold">Total</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step={1}
                    value={totalRemotas}
                    onChange={(e) => atualizarTotalRemotas(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">Observação</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Observações (opcional)"
                    value={novaObservacaoAtendimentoRemoto}
                    onChange={(e) => setNovaObservacaoAtendimentoRemoto(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <BotaoAdicionar className="w-100" onClick={adicionarAtendimentoRemoto} />
              </Col>
            </Row>
          ) : (
            <Row className="mb-3">
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="fw-bold">Total</Form.Label>
                  <Form.Control type="number" min={0} value={totalRemotas} disabled />
                </Form.Group>
              </Col>
            </Row>
          )}

          <Table bordered hover>
            <thead className="table-light">
              <tr>
                <th style={{ width: '5%' }}>#</th>
                <th style={{ width: '35%' }}>Arquivo</th>
                <th style={{ width: '10%' }}>Total</th>
                <th style={{ width: '35%' }}>Observação</th>
                {!isViewMode && <th style={{ width: '15%' }}>Ações</th>}
              </tr>
            </thead>
            <tbody>
              {formData.atendimento.remotas.length === 0 ? (
                <tr>
                  <td colSpan={isViewMode ? 4 : 5} className="text-center text-muted">
                    Nenhum atendimento remoto adicionado
                  </td>
                </tr>
              ) : (
                formData.atendimento.remotas.map((item: AtendimentoRemotoItem, index: number) => {
                  const estaEditando = editandoAtendimentoRemoto === item.id;

                  return (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>
                        {estaEditando ? (
                          <div>
                            <Form.Control
                              type="file"
                              size="sm"
                              ref={arquivoAtendimentoRemotoEdicaoInputRef}
                              onChange={handleFileChangeAtendimentoRemotoEdicao}
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              style={{ borderRadius: '8px' }}
                            />
                            {valorEdicaoArquivoAtendimentoRemoto && (
                              <small className="text-muted d-block mt-1">
                                {valorEdicaoArquivoAtendimentoRemoto.name} (
                                {(valorEdicaoArquivoAtendimentoRemoto.size / 1024 / 1024).toFixed(2)} MB)
                              </small>
                            )}
                            {!valorEdicaoArquivoAtendimentoRemoto && item.arquivoNome && (
                              <small className="text-muted d-block mt-1">Arquivo atual: {item.arquivoNome}</small>
                            )}
                          </div>
                        ) : item.arquivoNome ? (
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-file-earmark"></i>
                            <span>{item.arquivoNome}</span>
                            {item.arquivo && (
                              <Badge style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                                {(item.arquivo.size / 1024 / 1024).toFixed(2)} MB
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">Sem arquivo</span>
                        )}
                      </td>
                      {index === 0 && (
                        <td
                          className="text-center align-middle"
                          rowSpan={formData.atendimento.remotas.length}
                        >
                          {totalRemotas}
                        </td>
                      )}
                      <td>
                        {estaEditando ? (
                          <Form.Control
                            type="text"
                            size="sm"
                            placeholder="Observações (opcional)"
                            value={valorEdicaoObservacaoAtendimentoRemoto}
                            onChange={(e) => setValorEdicaoObservacaoAtendimentoRemoto(e.target.value)}
                            style={{ borderRadius: '8px' }}
                          />
                        ) : (
                          item.observacao || '-'
                        )}
                      </td>
                      {!isViewMode && (
                        <td>
                          {estaEditando ? (
                            <div className="d-flex align-items-center justify-content-center flex-nowrap gap-1">
                              <BotaoAcaoLinha tipo="salvar" onClick={() => salvarEdicaoAtendimentoRemoto(item.id)} />
                              <BotaoAcaoLinha tipo="cancelar" onClick={cancelarEdicaoAtendimentoRemoto} />
                            </div>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center flex-nowrap gap-1">
                              <BotaoAcaoLinha tipo="editar" onClick={() => iniciarEdicaoAtendimentoRemoto(item)} />
                              <BotaoAcaoLinha tipo="excluir" onClick={() => removerAtendimentoRemoto(item.id)} />
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
          <div className="mt-2 text-end">
            <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
              <small className="text-muted">Total:</small> {totalRemotas}
            </h5>
          </div>
        </Card.Body>
      </Card>
    </Fragment>
  );
}
